import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/ui/status-badge";
import type { AcqInvoiceDetail } from "@/lib/api/acquisitions";

type StatusCfg = { variant: "success" | "warning" | "info" | "danger" | "neutral"; label: string };

type AcqInvoiceSummaryProps = {
  invoice: AcqInvoiceDetail;
  statusCfg: StatusCfg;
  formatCurrency: (n: number) => string;
};

export function AcqInvoiceSummary({ invoice, statusCfg, formatCurrency }: AcqInvoiceSummaryProps) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-5">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge variant={statusCfg.variant}>{statusCfg.label}</StatusBadge>
          <span className="text-sm text-slate-500">{invoice.vendor.name}</span>
        </div>
        <p className="text-2xl font-semibold tabular-nums tracking-tight text-slate-900">
          {formatCurrency(Number(invoice.totalAmount))}
        </p>
        <p className="text-xs text-slate-500">
          {invoice.currency || "NGN"} · Invoice {invoice.invoiceNumber}
        </p>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:text-right">
        <div>
          <dt className="text-xs text-slate-500">Invoice date</dt>
          <dd className="font-medium text-slate-800">
            {new Date(invoice.invoiceDate).toLocaleDateString()}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Due date</dt>
          <dd className="font-medium text-slate-800">
            {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : "—"}
          </dd>
        </div>
        <div className="col-span-2">
          <dt className="text-xs text-slate-500">Purchase order</dt>
          <dd className="font-medium text-slate-800">
            {invoice.purchaseOrder ? (
              <Link
                to={`/acquisitions/orders/${invoice.purchaseOrderId}`}
                className="text-primary hover:underline"
              >
                {invoice.purchaseOrder.orderNumber}
              </Link>
            ) : (
              "—"
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}
